import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";

import { ButtonPrimary } from "src/UI/ButtonPrimary";
import { ButtonSecondary } from "src/UI/ButtonSecondary";
import {
  BrandAssetVariableRequest,
  BrandAssetVariableResponse,
  BrandAssetVariableType,
} from "../types";
import {
  DEFAULT_GRADIENT_END_VALUE,
  DEFAULT_GRADIENT_START_VALUE,
  DEFAULT_SOLID_VALUE,
  GRADIENT_END_SUFFIX,
  GRADIENT_START_SUFFIX,
  VARIABLE_NAME_REGEX,
  applyHexToRgbaTuple,
  buildCssGradient,
  isHexColor,
  normalizeHexTo6,
  rgbaTupleToHex6,
  toCssColor,
} from "../constants";
import { ColorPickerInput, ColorSwatch, colorRowSx } from "./ColorPickerBox";

interface VariableEditorDialogProps {
  open: boolean;
  corporateId: number;
  existingNames: string[];
  createBrandAssetVariable: (
    body: BrandAssetVariableRequest,
  ) => Promise<BrandAssetVariableResponse>;
  onClose: () => void;
  onCreated: (response: BrandAssetVariableResponse) => void;
}

const VariableEditorDialog: React.FC<VariableEditorDialogProps> = ({
  open,
  corporateId,
  existingNames,
  createBrandAssetVariable,
  onClose,
  onCreated,
}) => {
  const { t } = useTranslation(["corporate-brand-asset"]);
  const [type, setType] = useState<BrandAssetVariableType>("COLOR");
  const [name, setName] = useState("");
  const [value, setValue] = useState(DEFAULT_SOLID_VALUE);
  const [endValue, setEndValue] = useState(DEFAULT_GRADIENT_END_VALUE);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>();

  useEffect(() => {
    if (!open) return;
    setType("COLOR");
    setName("");
    setValue(DEFAULT_SOLID_VALUE);
    setEndValue(DEFAULT_GRADIENT_END_VALUE);
    setSubmitting(false);
    setError(undefined);
  }, [open]);

  const isGradient = type === "GRADIENT";

  const handleTypeChange = (next: BrandAssetVariableType) => {
    setType(next);
    setValue(
      next === "GRADIENT" ? DEFAULT_GRADIENT_START_VALUE : DEFAULT_SOLID_VALUE,
    );
    setEndValue(DEFAULT_GRADIENT_END_VALUE);
  };

  const trimmedName = name.trim();
  const createdNames = !trimmedName
    ? []
    : isGradient
      ? [
          `${trimmedName}${GRADIENT_START_SUFFIX}`,
          `${trimmedName}${GRADIENT_END_SUFFIX}`,
        ]
      : [trimmedName];

  const duplicates = createdNames.filter((n) => existingNames.includes(n));
  const isDuplicate = duplicates.length > 0;
  const isInvalidName = !!trimmedName && !VARIABLE_NAME_REGEX.test(trimmedName);
  const canSave =
    !!trimmedName &&
    !isDuplicate &&
    !isInvalidName &&
    !!value.trim() &&
    (!isGradient || !!endValue.trim()) &&
    !submitting;

  const handleSave = async () => {
    setSubmitting(true);
    setError(undefined);
    try {
      const response = await createBrandAssetVariable({
        corporateId,
        name: trimmedName,
        value: value.trim(),
        ...(isGradient ? { endValue: endValue.trim() } : {}),
        type,
      });
      onCreated(response);
    } catch (e) {
      setError(t("variables.saveError"));
    } finally {
      setSubmitting(false);
    }
  };

  const nameHelperText = isDuplicate
    ? t("variables.duplicate", { list: duplicates.join(", ") })
    : isInvalidName
      ? t("variables.invalidName")
      : undefined;
  const showGradientNames = isGradient && !!trimmedName && !isDuplicate;

  const renderStop = (
    label: string,
    stopValue: string,
    setStopValue: (next: string) => void,
  ) => (
    <Box sx={colorRowSx}>
      <ColorPickerInput
        value={rgbaTupleToHex6(stopValue)}
        ariaLabel={label}
        onChange={(hex6) => setStopValue(applyHexToRgbaTuple(stopValue, hex6))}
      />
      <TextField
        size="small"
        fullWidth
        required
        label={label}
        value={stopValue}
        onChange={(e) => setStopValue(e.target.value)}
      />
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          border: "1px solid",
          borderColor: "divider",
        },
      }}
    >
      <DialogTitle>{t("variables.createTitle")}</DialogTitle>
      <DialogContent
        dividers
        sx={{
          mx: 3,
          px: 0,
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography variant="body2">{t("variables.description")}</Typography>

          <FormControl size="small" fullWidth>
            <InputLabel id="brand-asset-variable-type-label">
              {t("variables.typeLabel")}
            </InputLabel>
            <Select
              labelId="brand-asset-variable-type-label"
              label={t("variables.typeLabel")}
              value={type}
              onChange={(e) =>
                handleTypeChange(e.target.value as BrandAssetVariableType)
              }
            >
              <MenuItem value="COLOR">{t("variables.typeColor")}</MenuItem>
              <MenuItem value="GRADIENT">
                {t("variables.typeGradient")}
              </MenuItem>
            </Select>
          </FormControl>

          <TextField
            size="small"
            fullWidth
            required
            label={
              isGradient ? t("variables.gradientName") : t("colors.addName")
            }
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={isDuplicate || isInvalidName}
            helperText={nameHelperText}
          />

          {!isGradient && (
            <Box sx={colorRowSx}>
              {isHexColor(value) ? (
                <ColorPickerInput
                  value={normalizeHexTo6(value)}
                  ariaLabel={String(t("colors.addValue"))}
                  onChange={setValue}
                />
              ) : (
                <ColorSwatch color={toCssColor(value)} />
              )}
              <TextField
                size="small"
                fullWidth
                required
                label={t("colors.addValue")}
                value={value}
                onChange={(e) => setValue(e.target.value)}
              />
            </Box>
          )}

          {isGradient && (
            <>
              {renderStop(t("variables.stopStart"), value, setValue)}
              {renderStop(t("variables.stopEnd"), endValue, setEndValue)}
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                {showGradientNames && (
                  <Typography variant="caption" color="text.primary">
                    {t("variables.gradientNamesHint", {
                      list: createdNames.join(", "),
                    })}
                  </Typography>
                )}
                <Typography variant="caption" color="text.primary">
                  {t("variables.tupleHint")}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <ColorSwatch
                  gradient={buildCssGradient(value, endValue)}
                  width={56}
                />
                <Typography variant="caption">
                  {t("variables.gradientPreview")}
                </Typography>
              </Box>
            </>
          )}

          {error && <Alert severity="error">{error}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <ButtonSecondary
          id="brand-asset-variable-cancel"
          size="md"
          onClick={onClose}
        >
          {t("cancel")}
        </ButtonSecondary>
        <ButtonPrimary
          id="brand-asset-variable-save"
          size="md"
          onClick={handleSave}
          disabled={!canSave}
        >
          {t("saveButton")}
        </ButtonPrimary>
      </DialogActions>
    </Dialog>
  );
};

export default VariableEditorDialog;
